
import { defineComponent, ref, onBeforeMount, watch, getCurrentInstance } from "vue";
import { apiService } from "@services/api.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import type { MetaInfo } from "vue-meta";

import CiContentsActions from "@components/contents/ContentsActions.vue";
import CiContentsArticle from "@components/contents/ContentsArticle.vue";
import CiContentsSidebar from "@components/contents/ContentsSidebar.vue";
import SliderStickyWrapper from "@components/base/slider-sticky-wrapper.vue";
import { useRoute } from "vue-router/composables";
import { Loader, useLoader } from "@composables/loader";
import { useI18n } from "@composables/i18n";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { Constants } from "@app/constants";
import { alternateService } from "@services/alternate.service";
import { ImgixService } from "@services/imgix.service";
import SharingLogo from "@images/logos/sharing/logo_white.png";

export default defineComponent({
  name: "CiContentsShow",
  components: {
    CiContentsActions,
    CiContentsArticle,
    CiContentsSidebar,
    "ci-slider-sticky-wrapper": SliderStickyWrapper,
  },
  props: {
    isPreview: { type: Boolean, default: false },
  },
  setup() {
    const route = useRoute();
    const loader = useLoader();
    const slug = route.params?.slug;
    const key = route.query?.key;
    const content = ref<Contents.Content>();
    const relatedProducts = ref<Products.TileData[]>([]);
    const adIds = {
      header: ADS_INVENTORY.article_detail_header_slot.dom_id,
      skyscraper: ADS_INVENTORY.article_detail_sidebar_top.dom_id,
      sidebar_2nd: ADS_INVENTORY.article_detail_sidebar_2nd.dom_id,
    };

    const trackPageView = async () => {
      const contentId = content.value?.id ? content.value?.id.toString() : ("" as string);

      await ciTrackingService.trackView(
        content.value?.content_type as string,
        contentId,
        null,
        null,
        content.value?.gated ? Constants.ciTracking.eventValueGated : Constants.ciTracking.eventValueAccessible
      );
    };

    onBeforeMount(async () => fetchContent(slug, key as Nullable<string>));

    watch(
      () => route.params.slug,
      async (newSlug) => fetchContent(newSlug, key as Nullable<string>)
    );

    watch(
      () => route.query?.key,
      async (newKey) => fetchContent(slug, newKey as Nullable<string>)
    );

    const fetchContent = async (slug: string, key: Nullable<string>) => {
      loader.start();

      try {
        const { data: articleData } = await apiService.articles.getContent(slug, key);
        const { data: relatedProductsData } = await apiService.articles.getRelatedProducts(articleData.id);

        content.value = articleData;
        relatedProducts.value = relatedProductsData.products;

        await trackPageView();
      } finally {
        loader.stop();
      }
    };

    return {
      adIds,
      content,
      relatedProducts,
    };
  },
  metaInfo(): MetaInfo {
    if (!this.content?.id) return {};

    const authorType = this.content?.copyright?.author?.name ? "Person" : "Organization";
    const authorName = this.content?.copyright?.author?.name || this.content?.copyright?.company?.name || "CAPinside";

    const link: Array<any> = this.metaOptions.metaAlternates;
    // add null for vmid canonical to prevent canonical rendering for external articles
    // see: https://vue-meta.nuxtjs.org/guide/special.html#remove-parent-property-by-child
    if (!this.metaOptions.metaCanonical) {
      link.push({ vmid: "canonical", rel: "", href: null });
    }
    return {
      title: this.metaOptions.metaTitle,
      link: link,
      meta: [
        { vmid: "robots", name: "robots", content: this.metaOptions.metaRobots },
        { vmid: "description", name: "description", content: this.metaOptions.metaDescription },
        { vmid: "og:title", property: "og:title", content: this.metaOptions.metaTitle },
        { vmid: "og:type", property: "og:type", content: "article" },
        { vmid: "og:description", property: "og:description", content: this.metaOptions.metaDescription },
        { vmid: "og:image", property: "og:image", content: this.metaOptions.metaImage },
        { vmid: "twitter:creator", name: "twitter:creator", content: "@CAPinside" },
        { vmid: "twitter:title", name: "twitter:title", content: this.metaOptions.metaTitle },
        { vmid: "twitter:description", name: "twitter:description", content: this.metaOptions.metaDescription },
        { vmid: "twitter:image", name: "twitter:image", content: this.metaOptions.metaImage },
      ],
      script: [
        {
          id: "contents#show-ld+json",
          type: "application/ld+json",
          json: {
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            "mainEntityOfPage": { "@type": "WebPage", "@id": null },
            "headline": this.content?.title,
            "image": [this.content?.hero_image.url],
            "datePublished": `${this.content?.publication_date}`,
            "dateModified": `${this.content?.updated_at}`,
            "author": { "@type": `${authorType}`, "name": `${authorName}` },
            "publisher": {
              "@type": "Organization",
              "name": "CAPinside.com",
              "logo": {
                "@type": "ImageObject",
                "url": window.location.origin + SharingLogo,
              },
            },
            "isAccessibleForFree": this.content?.gated ? "False" : "True",
            "hasPart": {
              "@type": "WebPageElement",
              "isAccessibleForFree": this.content?.gated ? "False" : "True",
              "cssSelector": ".paywall",
            },
          },
        },
      ],
    };
  },
  computed: {
    loading(): boolean {
      return Loader.loading.state;
    },
    routeHasArchiveQuery(): boolean {
      const route = useRoute();
      return route.query?.hasOwnProperty("nl_archiv_id");
    },
    currentURL() {
      return window.location.origin;
    },
    trackingData() {
      return {
        sourceName: this.content?.content_type,
        sourceIdentifier: this.content?.id,
        sourceUrl: this.content?.sharing_url,
        targetUrl: this.content?.sharing_url,
      };
    },
    metaOptions() {
      const { t } = useI18n(getCurrentInstance());
      return {
        metaTitle: this.content?.meta_title || this.content?.title || (t("ui.shared.global.meta_tags.title") as string),
        metaDescription:
          this.content?.meta_description ||
          this.content?.teaser ||
          (t("ui.shared.global.meta_tags.description") as string),
        metaRobots:
          this.isPreview || this.content?.gated || this.content?.external ? "noindex, follow" : "index, follow",
        metaImage: this.content?.hero_image.url || "",
        metaCanonical: !this.content?.external,
        metaAlternates: this.content ? alternateService.fromAlternateMap(this.content.alternate_urls) : [],
      };
    },
    heroImgSrcSet() {
      if (!this.content?.hero_image?.url) return "";
      if (this.content.hero_image?.url.match(/^(http|https|data):/)) return "";
      const options: Indexable = { minWidth: 320, maxWidth: 1243, ar: "2:1" };
      if (this.content.hero_image.focus_x && this.content.hero_image.focus_x !== 0.0)
        options.focus_x = this.content.hero_image.focus_x;
      if (this.content.hero_image.focus_y && this.content.hero_image.focus_y !== 0.0)
        options.focus_y = this.content.hero_image.focus_y;
      if (options.focus_x || options.focus_y) {
        options.type = "focalpoint";
        options.fit = "crop";
      } else options.fit = "clip";
      return ImgixService.buildSrcSet(this.content.hero_image?.url, options);
    },
  },
});
